// utils/loadCampaign.js
// NEW: Import specific API modules
import { campaignAPI, facilityAPI, squadAPI, researchAPI } from '../api';

/**
 * Loads all campaign-related data (facilities, squad, research)
 * @param {number} campaignId - The campaign ID
 * @returns {Promise<Object>} - Object containing facilities, squad, research, and activeResearch
 */
export const loadCampaignData = async (campaignId) => {
  try {
    // Load all data in parallel for better performance
    const [facilitiesData, squadData, researchData] = await Promise.all([
      facilityAPI.getFacilities(campaignId),
      squadAPI.getSquad(campaignId),
      researchAPI.getResearch(campaignId)
    ]);

    // Find active research
    const activeResearch = researchData.find(r => r.completed === 0);
    const currentResearch = activeResearch ? {
      id: activeResearch.id,
      name: activeResearch.research_name,
      type: activeResearch.research_type,
      progress: activeResearch.progress,
    } : null;

    // Ensure all facilities have is_active flag (backward compatibility)
    // Convert database boolean values (0/1) to JavaScript booleans
    const facilities = facilitiesData.map(facility => ({
      ...facility,
      is_active: facility.is_active === 1 || facility.is_active === true || facility.is_active === undefined
    }));

    return {
      facilities: facilities,
      squad: squadData,
      research: researchData,
      activeResearch: currentResearch
    };
  } catch (error) {
    console.error('Error loading campaign data:', error);
    throw error;
  }
};

/**
 * Loads a campaign from navigation state or API
 * @param {Object} locationState - Router location.state object
 * @param {number} userId - Current user ID
 * @returns {Promise<Object>} - Campaign object with resources
 */
export const loadCampaign = async (locationState, userId) => {
  try {
    let campaign = null;

    // Check if campaign was passed from navigation (new campaign)
    if (locationState?.campaign) {
      campaign = locationState.campaign;
    } 
    // Otherwise, try to load active campaign from API
    else if (userId) {
      campaign = await campaignAPI.getActiveCampaign(userId);
      
      if (!campaign) {
        return null;
      }
    } else {
      return null;
    }

    // Load all campaign data
    const campaignData = await loadCampaignData(campaign.id);

    // Build resources object
    const resources = {
      funds: campaign.funds || 25000,  // Use campaign funds, fallback to 25000
      supplies: campaign.supplies || 100,
      intel: campaign.intel || 0,
      power: 85
    };

    return {
      campaign,
      resources,
      facilities: campaignData.facilities,
      squad: campaignData.squad,
      research: campaignData.research,
      activeResearch: campaignData.activeResearch
    };
  } catch (error) {
    console.error('Error loading campaign:', error);
    throw error;
  }
};

/**
 * Gets the current user from localStorage
 * @returns {Object|null} - Current user object or null
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('currentUser');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing current user:', error);
    return null;
  }
};